import classNames from 'classnames'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext} from 'react'
import {BsPencilSquare, BsTrash} from 'react-icons/bs'
import {IoIosAddCircleOutline} from 'react-icons/io'
import {Link} from 'react-router-dom'
import {DelayRender} from 'views/Discovery/DelayRender'
import {CodiCurrentLinks} from 'views/Document/components/Codi/CodiDocumentLinks'
import {RequiredSystemAdmin} from 'components/RequiredAdmin'
import {NullAsDefault, renderIf} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import createContextMenu from '../factory/createContextMenu'
import CodiContext from './CodiContext'
import CodiKeywords from './CodiKeywords'
import CodiLinks from './CodiLinks'
import CodiNotes, {CodiNotesContext} from './CodiNotes'
import {RiNewspaperLine} from "react-icons/ri";
import {renderColon} from "../../Shared";
import ColonChar from "../../Shared/components/ColonChar";
import {GiIronCross} from "react-icons/gi";
import {getLinkToDetailRow} from "./codiHelpers";
import {formatDate} from "../../../helpers/datetimeHelpers";
import LoadingBlock from "../../LoadingBlock";

export const SelectedCodiRow = () => {
    const t = useTranslate()
    const {asyncRowDetail, system_row: item} = useContext(CodiContext)
    const isLoading = Boolean(asyncRowDetail?.isLoading)
    if (isLoading) {
        return <LoadingBlock/>
    }
    return renderIf(
        item,
        <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
                <div className="text-sm text-color-300 underline italic">
                    {`${t('content selected')}`}{renderColon()}
                </div>
                <div
                    style={{background: '#fff9d8'}}
                    className="flex flex-col px-2 py-1 rounded-md">
                    {item?.short_index_name && item?.index_typecd !== 'root' && (
                        <span className="font-bold text-xs text-red-600 capitalize italic">
              {item?.short_index_name}
                            {renderIf(item?.content, <ColonChar/>)}
            </span>
                    )}
                    <span className="text-sm text-color-100 italic ml-2">
            {item?.content}
          </span>
                </div>
            </div>
        </div>
    )
}

const NotesHeader = () => (
    <CodiNotesContext.Consumer>
        {({handleAction = NullAsDefault}) => (
            <div className="flex items-center border-b">
                <Translate>
                    {(t) => (
                        <span className="flex-1 uppercase font-medium text-sm text-color-300">
              {t('notes')}
            </span>
                    )}
                </Translate>
                <RequiredSystemAdmin type={"codi_document"}>
                    <IoIosAddCircleOutline
                        size={17}
                        className="text-color-200 hover:text-primary cursor-pointer"
                        onClick={(event) => {
                            event.stopPropagation(event)
                            handleAction(CRUD.CREATE)
                        }}
                    />
                </RequiredSystemAdmin>
            </div>
        )}
    </CodiNotesContext.Consumer>
)

export const SystemSelectedRow = () => {
    const t = useTranslate()
    const {
        id,
        onRefresh,
        filterActions,
        asyncRowDetail,
        system_row: item,
    } = useContext(CodiContext)

    const isLoading = Boolean(asyncRowDetail?.isLoading)
    if (isLoading) {
        return <LoadingBlock/>
    }
    return renderIf(
        item,
        <div className="flex flex-col gap-8">
            <SelectedCodiRow/>
            {[
                {
                    label: 'notes',
                    Placeholder: NullAsDefault,
                    Component: CodiNotes,
                    renderHeader: () => <NotesHeader/>,
                    renderContextMenu: (item) => (
                        <CodiNotesContext.Consumer>
                            {({handleAction = NullAsDefault}) =>
                                createContextMenu(
                                    [
                                        {
                                            icon: <BsPencilSquare/>,
                                            label: <span className="font-medium">{t('edit')}</span>,
                                            key: CRUD.UPDATE,
                                        },
                                        {
                                            icon: <BsTrash/>,
                                            label: <span className="font-medium">{t('delete')}</span>,
                                            key: CRUD.DELETE,
                                        },
                                    ],
                                    (key) => {
                                        handleAction(key, item)
                                    }
                                )
                            }
                        </CodiNotesContext.Consumer>
                    ),
                },
                {label: 'links', Component: CodiLinks, Placeholder: NullAsDefault},
                {label: 'keywords', Component: CodiKeywords, Placeholder: NullAsDefault},
            ].map(
                (
                    {
                        label,
                        Component = NullAsDefault,
                        renderHeader = (child, label) => child,
                        ...rest
                    },
                    index
                ) => (
                    <Component
                        key={index}
                        {...rest}
                        {...{
                            onRefresh,
                            row: item,
                            codi_id: id,
                            filterActions,
                            render: (child) => (
                                <div className="flex flex-col gap-2">
                                    {renderHeader(
                                        <div className="flex uppercase font-medium text-sm text-color-300 border-b">
                                            {`${t(label)}`}
                                        </div>,
                                        label
                                    )}
                                    {child}
                                </div>
                            ),
                        }}
                    />
                )
            )}
        </div>
    )
}

const MentionArticle = () => {
    const {system_row: item} = useContext(CodiContext)
    const mention_articles = _.get(item, 'mention_articles', [])

    return renderIf(
        mention_articles.length > 0,
        <Translate>
            {(t) => (
                <div className="flex flex-col gap-2">
                    <div className="font-medium text-sm text-color-300 uppercase border-b">
                        {t('the content of the article being referenced')}
                    </div>
                    <div className="space-y-2">
                        {mention_articles.map((e, i) => (
                            <div
                                key={i}
                                className="flex flex-col p-2 border border-color-50 rounded-md hover:shadow-items">
                                <Link
                                    className="flex items-baseline gap-2 text-sm text-color-000 font-medium no-underline leading-tight"
                                    to={getLinkToDetail(e)}>
                                    <RiNewspaperLine
                                        size={14}
                                        className="text-color-400"
                                    />
                                    <span className="flex-1">{e.title}</span>
                                </Link>
                                <div className="flex flex-1 gap-1 text-2xs mt-1">
                  <span className="font-light text-color-400 italic">
                    {t('written by')}
                  </span>
                                    <Link
                                        className="text-primary font-bold flex-1"
                                        to={getLinkToDetail(e.owner)}>
                                        {getTitle(e.owner)}
                                    </Link>
                                    <span className="text-color-400 italic">
                    {formatDate(e.created, 'HH:mm - DD/MM/YYYY')}
                  </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Translate>
    )
}

const CodiOtherLanguage = () => {

    const {system_row: item} = useContext(CodiContext)
    const other_language_codi = _.get(item, 'other_language_codi_document_rows', [])

    return renderIf(
        other_language_codi.length > 0,
        <Translate>
            {(t) => (
                <div className="flex flex-col gap-2">
                    <div className="font-medium text-sm text-color-300 uppercase border-b">
                        {t('codi in other languages')}
                    </div>
                    <div className="space-y-3">
                        {other_language_codi.map((e, i) => {
                            const {codi_document} = e ?? {}

                            return (
                                <div key={i} className="flex flex-col">
                                    <div className="flex items-baseline gap-1 leading-tight">
                                        <GiIronCross size={10} className="text-primary-400"/>
                                        <Link target="_blank" to={getLinkToDetailRow(e)}
                                              className="flex-1 text-color-000 no-underline">
                                            <span className="font-bold text-sm">
                                                {getTitle(codi_document)}
                                            </span>
                                            <span className="ml-1 font-semibold text-xs text-color-200">
                                                {`(${codi_document?.document_no})`}
                                            </span>
                                        </Link>
                                    </div>
                                    <div style={{marginLeft: 15}} className="py-1 text-xs italic max-lines-3">
                                        <label
                                            className="font-medium text-color-200 capitalize whitespace-no-wrap pr-1">
                                            {`${e?.short_index_name}: `}
                                        </label>
                                        <label className="text-color-400">
                                            {e?.content}
                                        </label>
                                    </div>
                                    <p className="text-right font-medium text-doc-xs text-color-200 leading-tight">
                                        {codi_document?.version} {' - '}
                                        <label className="font-normal text-blue-500 italic">
                                            {t(codi_document?.language)}
                                        </label>
                                    </p>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </Translate>
    )
}

const CodiCurrentDetail = ({style, className}) => {
    const {
        item,
        asyncRowDetail,
        isLoading = false,
        system_row,
    } = useContext(CodiContext)

    if (isLoading || asyncRowDetail?.isLoading) {
        return <LoadingBlock/>
    }
    return (
        <div
            style={style}
            className={classNames('flex flex-col gap-8', className)}>
            <SystemSelectedRow/>
            <DelayRender lazy={true}>
                <CodiCurrentLinks
                    item={item}
                    current_row={system_row}
                    target={system_row ?? item}
                />
            </DelayRender>
            <MentionArticle/>
            <CodiOtherLanguage/>
            <div className="h-12"/>
        </div>
    )
}

export default CodiCurrentDetail
